import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getShouldRenderBelowSpinner } from '~/selectors';
import useIsInMobileStickyNavExperiment from './useIsInMobileStickyNavExperiment';
import useIsScrolling from './useIsScrolling';

/**
 *  useBannerState handles the state for the sticky banner
 *  and back to top cta (only on mobile sticky nav experiment currently)
 *  On intial stick banner showing, back to top cta will also show
 *  If back to top is showing and user isn't scrolling, we wait 1.5 seconds
 *  then hide the back to top cta.
 *  If user starts scrolling again while the sticky banner is opened, the back to top
 *  cta is shown again.
 */

const OneAndAHalfSeconds = 1500;

const useBannerState = () => {
  const trigger = useSelector(getShouldRenderBelowSpinner);
  const isInStickyNavExperiment = useIsInMobileStickyNavExperiment();
  const [showBanner, setShowBanner] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const isScrolling = useIsScrolling();

  useEffect(() => {
    if (
      (document?.documentElement?.scrollTop || document?.body?.scrollTop) > 250
    ) {
      setShowBanner(true);
      if (isInStickyNavExperiment) {
        setShowBackToTop(true);
      }
    }
  }, [isInStickyNavExperiment, trigger]);

  useEffect(() => {
    if (!isInStickyNavExperiment) return;

    if (!showBackToTop && isScrolling && showBanner) {
      setShowBackToTop(true);
    }

    if (!isScrolling && showBackToTop) {
      const timeoutId = setTimeout(() => {
        setShowBackToTop(false);
      }, OneAndAHalfSeconds);

      return () => clearTimeout(timeoutId);
    }
  }, [isInStickyNavExperiment, isScrolling, showBackToTop, showBanner]);

  const handleBannerTransition = useCallback(
    ({ currentPosition, previousPosition }) => {
      if (currentPosition === 'above' && previousPosition === 'inside') {
        setShowBanner(true);
        if (isInStickyNavExperiment) {
          setShowBackToTop(true);
        }
      }
      if (currentPosition === 'inside' && previousPosition === 'above') {
        setShowBanner(false);
        if (isInStickyNavExperiment) {
          setShowBackToTop(false);
        }
      }
    },
    [isInStickyNavExperiment]
  );

  return { showBanner, showBackToTop, handleBannerTransition };
};

export default useBannerState;
